import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { useEffect, useMemo, useState } from 'react';
import { AgTable } from '../../../components/AgTable/AgTable';
import { FadeInGridWrapper } from '../../../components/grid-renderers/FadeInGridWrapper';
import { Loader } from '../../../components/Loader/Loader';
import { useToastMessageState } from '../../../components/ToastMessage/ToastMessageProvider';
import {
  ATLAS_GL_DELIVERY_FILE_GRID_FIELDS,
  AtlasGlDeliveryFile,
  atlasGlDeliveryFileSchema,
} from '../../../schemas/AtlasGlDeliveryFile.schema';
import {
  fetchAtlasAdminGlDeliveryFiles,
  fetchAtlasGlFileDownloadUrl,
} from '../../../services/queries/MaggieTransactionQueries';
import { getErrorMessage } from '../../../services/queryHelpers';
import { withOverrides } from '../../../util/ag-grid-utils';
import { useAsync } from '../../../util/hook-utils';
import { useSchemaToGrid } from '../../../util/schema-utils';
import { triggerBrowserDownload } from '../../DocumentHub/documentHub.utils';
import type { IDeliveryScope } from './TableDataDownload';

interface IAtlasSourceFilesSectionProps {
  onExportRows: (scope: IDeliveryScope) => void;
  onSourceObjectsLoaded?: (sourceObjects: string[]) => void;
}

/** Shown on the Export Data page when the Atlas Live Rows config is selected,
 *  so CS can find and download the underlying delivery a set of rows came
 *  from without leaving the export flow. */
export function AtlasSourceFilesSection({
  onExportRows,
  onSourceObjectsLoaded,
}: IAtlasSourceFilesSectionProps) {
  const { data, loading, error } = useAsync(fetchAtlasAdminGlDeliveryFiles, {
    id: 'atlas-source-files',
  });

  useEffect(() => {
    if (data && onSourceObjectsLoaded) {
      onSourceObjectsLoaded([...new Set(data.map((row) => row.sourceObject).filter(Boolean))].sort());
    }
  }, [data, onSourceObjectsLoaded]);

  return (
    <Box mt={3}>
      <Typography variant='subtitle2'>Source Files</Typography>
      <Typography variant='body2' color='text.secondary' gutterBottom>
        Download gets the original file. Export rows exports the rows Atlas currently reads from that
        delivery.
      </Typography>
      {error ? (
        <Alert severity='error'>Failed to load source files</Alert>
      ) : !data ? (
        loading && <Loader />
      ) : (
        <AtlasSourceFilesGrid data={data} onExportRows={onExportRows} />
      )}
    </Box>
  );
}

function AtlasSourceFilesGrid({
  data,
  onExportRows,
}: {
  data: AtlasGlDeliveryFile[];
  onExportRows: (scope: IDeliveryScope) => void;
}) {
  const columnDefs = useAtlasSourceFilesColDefs(onExportRows);
  return (
    <FadeInGridWrapper>
      <Stack height={gridHeight(data.length)}>
        <AgTable columnDefs={columnDefs} rowData={data} rowGroupPanelShow='never' />
      </Stack>
    </FadeInGridWrapper>
  );
}

// Fit the grid to its rows (header + rows) up to a cap, instead of a fixed tall box.
const HEADER_PX = 50;
const ROW_PX = 42;
function gridHeight(rowCount: number) {
  return `${Math.min(HEADER_PX + Math.max(rowCount, 1) * ROW_PX + 2, 400)}px`;
}

function useAtlasSourceFilesColDefs(onExportRows: (scope: IDeliveryScope) => void) {
  const schemaToGrid = useSchemaToGrid();
  return useMemo(() => {
    const schema = atlasGlDeliveryFileSchema();
    const baseCols = withOverrides(schemaToGrid(schema, [...ATLAS_GL_DELIVERY_FILE_GRID_FIELDS]), {
      sourceObject: { flex: 1 },
      fileName: {
        flex: 2,
        // No-file deliveries (e.g. QuickBooks) carry a synthetic path whose basename isn't a real file.
        valueGetter: (params) =>
          (params.data as AtlasGlDeliveryFile | undefined)?.hasSourceFile
            ? (params.data as AtlasGlDeliveryFile).fileName
            : 'No source file',
        cellStyle: (params) =>
          (params.data as AtlasGlDeliveryFile | undefined)?.hasSourceFile
            ? null
            : { color: 'var(--mui-palette-text-secondary, #888)' },
      },
      deliveryId: { width: 150 },
    });
    return baseCols.concat([
      {
        colId: 'actions',
        headerName: 'Actions',
        cellRenderer: AtlasSourceFileActionsRenderer,
        cellRendererParams: { onExportRows },
        sortable: false,
        filter: false,
        resizable: false,
        width: 200,
      },
    ]) as ColDef<AtlasGlDeliveryFile>[];
  }, [schemaToGrid, onExportRows]);
}

// Only rows with a stored source file can be downloaded — deliveries without
// files (e.g. QuickBooks) carry the empty-delivery placeholder shape instead.
function isDownloadable(
  data: AtlasGlDeliveryFile | undefined
): data is AtlasGlDeliveryFile & { deliveryId: number; fileIndex: number } {
  return !!data && data.hasSourceFile === true && data.fileIndex != null && data.deliveryId != null;
}

interface IAtlasSourceFileActionsRendererParams extends ICellRendererParams<AtlasGlDeliveryFile> {
  onExportRows?: (scope: IDeliveryScope) => void;
}

export function AtlasSourceFileActionsRenderer(params: IAtlasSourceFileActionsRendererParams) {
  const { pushErrorToast } = useToastMessageState();
  const [isDownloading, setIsDownloading] = useState(false);
  const { data, onExportRows } = params;

  const handleDownload = async () => {
    if (!isDownloadable(data)) {
      return;
    }
    setIsDownloading(true);
    try {
      const { url } = await fetchAtlasGlFileDownloadUrl(data.deliveryId, data.fileIndex);
      triggerBrowserDownload(url, data.fileName);
    } catch (error) {
      pushErrorToast({
        message: getErrorMessage(error, 'Failed to download the source file. Please try again.'),
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Stack direction='row' spacing={0.5}>
      {isDownloadable(data) && (
        <Button
          size='small'
          disabled={isDownloading}
          startIcon={isDownloading ? <CircularProgress size={14} /> : undefined}
          onClick={handleDownload}
        >
          Download
        </Button>
      )}
      {data?.deliveryId != null && data.status === 'completed' && (
        <Button
          size='small'
          onClick={() => onExportRows?.({ deliveryId: data.deliveryId, sourceObject: data.sourceObject })}
        >
          Export rows
        </Button>
      )}
    </Stack>
  );
}
